import { useLocation, Link, useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { useState, useImperativeHandle, forwardRef, useEffect, useRef } from 'react';
import { updateMetric } from '../../services/metricsService';
import { X as XIcon, Menu as MenuIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

//images
import headerLogo from '../../assets/icons/header_logo.svg';
import rightIcon from '../../assets/icons/right_icon.svg';
import renameIcon from '../../assets/icons/rename_icon.svg';
import linkIcon from '../../assets/icons/link_icon.svg';
import plusIcon from '../../assets/icons/plus_icon.svg';
import plusIconBlack from '../../assets/icons/plus_icon_black.svg';
import shareIcon from '../../assets/icons/share_icon.svg';

const Header = forwardRef((props, ref) => {
  const location = useLocation();
  const { id } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const [evaluationName, setEvaluationName] = useState('');
  const [metricId, setMetricId] = useState('');
  const [showCopyPopup, setShowCopyPopup] = useState(false);
  const [copyPopupPos, setCopyPopupPos] = useState({ x: 0, y: 0 });
  const popupTimeoutRef = useRef();
  const textareaRef = useRef();
  const [isLoading, setIsLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    getEvaluationName: () => evaluationName,
    setEvaluationName: (name) => setEvaluationName(name),
    setMetricId: (value) => setMetricId(value || ''),
    setLoading: (loading) => setIsLoading(loading)
  }));

  // Set loading to false after a short delay
  useEffect(() => {
    evaluationName && setIsLoading(false);
  }, [evaluationName]);

  const isAdminPage = location.pathname.includes('/admin');
  const showEvaluationControls = !isAdminPage;

  useEffect(() => {
    if (id && id !== metricId) {
      setMetricId(id);
    }
  }, [id, metricId]);

  const formattedId = metricId || id || '';
  const hasId = Boolean(formattedId);

  const handleCopyLink = (e) => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    if (e && e.clientX && e.clientY) {
      setCopyPopupPos({ x: e.clientX, y: e.clientY });
    }
    setShowCopyPopup(true);
    clearTimeout(popupTimeoutRef.current);
    popupTimeoutRef.current = setTimeout(() => setShowCopyPopup(false), 2000);
  };

  const handleNewEvaluation = () => {
    window.open('/metrics', '_blank');
  };

  const adjustTextareaHeight = (textarea) => {
    if (!textarea) return;

    textarea.style.height = 'auto';

    const maxHeight = 70;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${newHeight}px`;

    if (textarea.scrollHeight > maxHeight) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  };

  const handleNameChange = (e) => {
    setEvaluationName(e.target.value);
    adjustTextareaHeight(e.target);
  };

  const handleNameBlur = (e) => {
    e.target.value.length && setIsEditing(false);
  };

  const handleKeyDown = async (e) => {
    const isEmptyValue = !e.target.value.length;
    if (e.key === 'Enter') {
      !isEmptyValue && setIsEditing(false);
      try {
        await updateMetric(id, "name", evaluationName);
      } catch (error) {
        console.error('Failed to update name:', error);
      }
    } else if (e.key === 'Escape') {
      !isEmptyValue && setIsEditing(false);
    }
  };

  // Adjust textarea height when editing starts
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      adjustTextareaHeight(textareaRef.current);
    }
  }, [isEditing]);

  // Close menu on Escape
  useEffect(() => {
    if (!isMenuOpen) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isMenuOpen]);

  return (
    <header className="border-b bg-white sticky top-0 z-50 w-full h-[56px] md:h-[76px] md:shadow-[0px_4px_0px_white]">
      <div className="container mx-auto p-4 min-h-[56px] md:min-h-[76px] flex items-center justify-between h-[56px] md:h-[76px]">
        <div className="flex flex-col space-x-2 md:space-x-4 md:mr-4">
          <div className="flex items-center">
            <a href="/">
              <img src={headerLogo} alt="Alconost MT Logo" className="h-6 md:h-8" />
            </a>
            <>
              <img src={rightIcon} alt="Right arrow" className="h-4 w-4 text-gray-400 hidden sm:inline !ml-2 !mr-2" />
              <Link
                to={formattedId ? `/${formattedId}` : '/'}
                className="text-gray-500 hidden sm:inline cursor-default"
              >
                Metrics
              </Link>
              {isAdminPage && (
                <>
                  <img src={rightIcon} alt="Right arrow" className="h-4 w-4 text-gray-400 hidden sm:inline" />
                  <span className="text-gray-500 hidden sm:inline">Admin</span>
                </>
              )}
            </>
          </div>
          <p className="text-[10px] text-[#6B7280] !ml-[29px] md:!ml-[39px]">provided by
            <a href="https://alconost.com" target="_blank" rel="noopener noreferrer" className="text-[#3B82F6]"> Alconost </a>
            localization services provider
          </p>
        </div>
        {/* Burger/Close menu icon for mobile */}
        {showEvaluationControls && (
          <>
            <button className="md:hidden p-2" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label={isMenuOpen ? "Close menu" : "Open menu"}>
              {isMenuOpen ? (
                <XIcon className="h-6 w-6" />
              ) : (
                <MenuIcon className="h-6 w-6" />
              )}
            </button>
            <div className="hidden md:flex items-center divide-x">
              <div className={`flex flex-col pr-6 pt-0 min-h-[50px] w-full max-w-[360px] relative ${!hasId ? 'justify-center' : 'justify-start'}`}>
                <div className="flex items-center space-x-2 min-h-[29px] md:w-[280px] w-[340px] relative">
                  {isEditing ? (
                    <>
                      <textarea
                        ref={textareaRef}
                        value={evaluationName}
                        onChange={handleNameChange}
                        onBlur={handleNameBlur}
                        onKeyDown={handleKeyDown}
                        autoFocus
                        rows={1}
                        className="md:w-[280px] w-[340px] min-h-[22px] leading-[21px] text-lg font-medium focus-visible:outline-none resize-none bg-[#F1F5F9] rounded-md px-3 py-1 border border-[#E2E8F0] absolute z-10 [box-shadow:0_1px_2px_0_rgba(0,_0,_0,_0.05)]"
                        style={{
                          height: '29px',
                          overflowY: 'hidden',
                          top: '-1px'
                        }}
                      />
                      {!evaluationName.length &&
                        <p className="z-20 absolute bg-[#fff] p-[4px] rounded-[4px] left-[0] border-[1px] border-[solid] border-[#B6B6B6] text-sm !ml-0 top-[90%] [box-shadow:rgba(0,_0,_0,_0.2)_0_1px_4px]">
                          The project name can't be empty
                        </p>
                      }
                    </>
                  ) : (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => setIsEditing(true)}
                            className={`flex items-center space-x-2 hover:text-[#020817] w-full hover:bg-[#E2E8F0] rounded-md px-2 py-1 transition-colors ${!hasId ? 'justify-center text-center' : ''}`}
                          >
                            <span
                              className={`text-lg leading-[21px] font-medium max-h-[29px] overflow-hidden text-ellipsis whitespace-nowrap
                                ${isLoading ? 'invisible' : ''}
                              `}
                            >
                              {evaluationName}
                            </span>
                            <img src={renameIcon} alt="Rename icon" className={`h-4 w-4 text-gray-400 hover:text-gray-600 ${!isLoading ? '' : 'invisible'}`} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{evaluationName}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                {hasId && (
                  <span className={`text-sm text-gray-500 mt-1 leading-[16px] flex items-center gap-1 px-2 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
                    ID:
                    <span
                      className="flex items-center group cursor-pointer text-gray-500 hover:text-gray-700 relative"
                      onClick={handleCopyLink}
                      onMouseDown={e => handleCopyLink(e)}
                      tabIndex={0}
                      role="button"
                      onKeyPress={e => { if (e.key === 'Enter' || e.key === ' ') handleCopyLink(e); }}
                    >
                      <span
                        className="text-[#6B7280] group-hover:text-gray-700                  
                      md:max-w-[140px] whitespace-nowrap md:overflow-hidden md:text-ellipsis 
                      lg:max-w-full lg:overflow-visible"
                      >
                        {formattedId}
                      </span>
                      <span className="relative flex items-center">
                        <img src={linkIcon} alt="Link icon" className="h-4 w-4 ml-1 opacity-70 group-hover:opacity-100" />
                        {showCopyPopup && (
                          <div
                            className="fixed px-3 py-1 bg-gray-800 text-white text-sm rounded-md whitespace-nowrap z-50 shadow-lg pointer-events-none"
                            style={{ left: copyPopupPos.x, top: copyPopupPos.y + 12 }}
                          >
                            Link copied
                          </div>
                        )}
                      </span>
                    </span>
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-3 pl-6">
                <Button
                  size="sm"
                  onClick={handleNewEvaluation}
                  style={{ boxShadow: '0px 1px 2px 0px #0000000D' }}
                  className="flex items-center space-x-2 bg-white hover:bg-[#F1F5F9] text-[#020817] h-8 text-base border border-[#E2E8F0]"
                >
                  <img src={plusIconBlack} alt="Plus icon" className="h-4 w-4" />
                  <span>New Metrics Run</span>
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
      {/* Mobile burger menu overlay, now below header */}
      {
        isMenuOpen && (
          <div className="md:hidden fixed left-0 right-0 bottom-0 z-50 bg-black bg-opacity-40 flex" style={{ top: '56px' }}>
            <nav className="bg-white w-full h-full shadow-lg flex flex-col relative animate-slide-in-left" style={{ maxHeight: 322 }}>
              <div className="py-4 px-4 md:px-6 border-b overflow-y-auto" style={{ maxHeight: 322, background: '#F9FAFB', marginBottom: 4 }}>
                <div className="flex flex-col items-start space-y-1">
                  <span className="text-lg text-[#020817] font-bold flex items-center">
                    {evaluationName}
                    <button onClick={() => { setIsEditing(true); setIsMenuOpen(false); }} className="ml-2">
                      <img src={renameIcon} alt="Rename icon" className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    </button>
                  </span>
                  {hasId && (
                    <span className="text-base text-[#6B7280] break-all mb-2">ID: {formattedId}</span>
                  )}
                </div>
              </div>
              <div className="flex-1 flex flex-col py-2 px-4 md:px-6 divide-y divide-gray-200 overflow-y-auto" style={{ maxHeight: 322 }}>
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => { handleCopyLink(); setIsMenuOpen(false); }}
                  className="flex items-center space-x-2 w-full justify-start text-base text-gray-700" style={{ padding: '12px 0px' }}
                >
                  <img src={shareIcon} alt="Share icon" className="h-5 w-5" />
                  <span>Share</span>
                </Button>
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => { setIsEditing(true); setIsMenuOpen(false); }}
                  className="flex items-center space-x-2 w-full justify-start text-base text-gray-700" style={{ padding: '12px 0px' }}
                >
                  <img src={renameIcon} alt="Rename icon" className="h-5 w-5" />
                  <span>Rename</span>
                </Button>
              </div>
              <div className="px-4 md:px-6 pb-6 pt-1">
                <Button
                  size="lg"
                  onClick={() => { handleNewEvaluation(); setIsMenuOpen(false); }}
                  className="w-full flex items-center space-x-2 justify-center bg-[#020817] hover:bg-[rgb(17,24,39)]/90 text-white py-3 rounded-md text-base"
                >
                  <img src={plusIcon} alt="Plus icon" className="h-5 w-5" />
                  <span>New Metrics Run</span>
                </Button>
              </div>
            </nav>
          </div>
        )
      }
    </header >
  );
});

export default Header; 